<script lang="ts">
	// Bandeau de facturation, sous la barre, sur toutes les pages connectées :
	// il n'apparaît que lorsque l'abonnement attend un geste de l'enseignant —
	// une facture à régler après un prélèvement refusé, ou une carte qui expire.
	// Il prévient, il ne bloque rien : l'accès Pro est maintenu pendant que
	// Stripe relance la carte (voir quotas.ts, 'past_due').
	import { authState, refreshSubscriptionData } from '$lib/stores/auth.svelte';
	import { openBillingPortal } from '$lib/api';
	import { billingAlert } from '$lib/billing';
	import { CreditCard, X } from 'lucide-svelte';

	const { subscription } = authState;

	let alerte = $derived(billingAlert($subscription?.billing));
	// La carte qui expire peut se masquer le temps de la session ; une facture
	// impayée reste visible tant qu'elle n'est pas réglée.
	let masquee = $state(false);
	let ouverture = $state(false);
	let erreur = $state('');

	let visible = $derived(alerte !== null && !(masquee && alerte?.kind === 'carte'));

	// La facture s'ouvre dans un autre onglet : au retour, on relit le profil
	// pour que le bandeau disparaisse dès que Stripe a confirmé le paiement.
	$effect(() => {
		if (!visible || alerte?.kind !== 'impaye') return;
		const auRetour = () => {
			if (document.visibilityState === 'visible') refreshSubscriptionData();
		};
		document.addEventListener('visibilitychange', auRetour);
		return () => document.removeEventListener('visibilitychange', auRetour);
	});

	async function ouvrirPortail(flow?: 'payment_method') {
		if (ouverture) return;
		ouverture = true;
		erreur = '';
		try {
			await openBillingPortal(flow, 'bandeau');
		} catch {
			erreur = 'Une erreur est survenue. Veuillez réessayer.';
			ouverture = false;
		}
	}
</script>

{#if visible && alerte}
	<div
		class="border-b border-miel/25 bg-miel-pale"
		role={alerte.kind === 'impaye' ? 'alert' : 'status'}
	>
		<div class="container mx-auto flex flex-wrap items-center gap-x-4 gap-y-2 px-4 py-2.5">
			<CreditCard class="size-4 shrink-0 text-miel" aria-hidden="true" />
			<p class="min-w-0 flex-1 basis-64 text-sm leading-snug text-ink">
				{#if alerte.kind === 'impaye'}
					{#if alerte.suspended}
						<strong class="font-semibold">Votre abonnement attend un règlement</strong> et
						l'accès Pro est suspendu en attendant. Réglez la facture pour le retrouver
						aussitôt.
					{:else}
						<strong class="font-semibold">Votre dernier prélèvement n'a pas abouti.</strong>
						Rien ne change pour vous : il suffit de régler la facture, cela prend une minute.
					{/if}
				{:else if alerte.expired}
					<strong class="font-semibold">Votre carte bancaire a expiré.</strong>
					Mettez-la à jour pour que votre abonnement continue sans interruption.
				{:else}
					<strong class="font-semibold">Votre carte bancaire expire fin {alerte.expiration}.</strong>
					Mettez-la à jour pour que votre abonnement continue sans interruption.
				{/if}
				{#if erreur}
					<span class="block text-danger">{erreur}</span>
				{/if}
			</p>

			{#if alerte.kind === 'impaye' && alerte.url}
				<a
					href={alerte.url}
					target="_blank"
					rel="noopener"
					class="btn-relief inline-flex shrink-0 items-center px-3.5 py-1.5 text-sm font-semibold"
				>
					Régler ma facture
				</a>
			{:else if alerte.kind === 'impaye'}
				<button
					type="button"
					class="btn-relief inline-flex shrink-0 items-center px-3.5 py-1.5 text-sm font-semibold"
					onclick={() => ouvrirPortail()}
					disabled={ouverture}
				>
					{ouverture ? 'Ouverture…' : 'Voir ma facture'}
				</button>
			{:else}
				<button
					type="button"
					class="btn-relief inline-flex shrink-0 items-center px-3.5 py-1.5 text-sm font-semibold"
					onclick={() => ouvrirPortail('payment_method')}
					disabled={ouverture}
				>
					{ouverture ? 'Ouverture…' : 'Mettre à jour ma carte'}
				</button>
				<button
					type="button"
					aria-label="Masquer ce rappel"
					class="rounded p-1 text-ash transition-colors hover:bg-paper hover:text-ink"
					onclick={() => (masquee = true)}
				>
					<X class="size-4" />
				</button>
			{/if}
		</div>
	</div>
{/if}
